import { Box, Typography, Skeleton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useCustomTheme from '../../../theme/useCustomTheme';
import { useSelector } from 'react-redux';
import { getInitials } from '../../../utils/commonServices';
import { getContrastRatio } from '@mui/material/styles';
import CustomTooltip from '../../../components/CustomTooltip';
import '@fontsource/rubik';

export default function DashboardHeader({ summary, isLoading }) {
  const [theme] = useCustomTheme();
  const commonReducer = useSelector((state) => state.root.commonReducer);

  const orgName = summary?.org_name || commonReducer?.company_name || 'Your Organization';
  const renewalDate = summary?.renewal_date || 'NA';
  const renewsInDays = summary?.renews_in_days;
  const ownerName = summary?.account_owner_name || '—';
  const ownerEmail = summary?.account_owner_email || '—';

  const avatarBg = theme.palette.primary.main;
  const avatarColor =
    getContrastRatio(avatarBg, '#ffffff') > 2.5 ? '#ffffff' : '#000000';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: { xs: 2, lg: 3 },
        flexWrap: 'wrap',
        gap: 2,
        fontFamily: 'Rubik',
      }}
    >
      {/* ── Left: org + title + renewal + owner ── */}
      <Box>
        {isLoading ? (
          <>
            <Skeleton width={320} height={40} sx={{ mb: 1 }} />
            <Skeleton width={180} height={14} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '22px', md: '26px', lg: '30px', xl: '36px' },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: '#08231B',
                  lineHeight: 1.1,
                }}
              >
                {orgName ? `${orgName} Account Dashboard` : 'Account Dashboard'}
              </Typography>
              <CustomTooltip title="Recent project activity may take upto 30 minutes to appear." placement="top">
                <InfoOutlinedIcon sx={{ fontSize: { xs: '15px', lg: '17px', xl: '20px' }, color: '#6b7470', cursor: 'pointer' }} />
              </CustomTooltip>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '11px', lg: '12px', xl: '14px' },
                  color: '#6b7470',
                }}
              >
                {renewsInDays != null ? `Renews in ${renewsInDays} days` : 'Renewal: NA'}
              </Typography>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: '#a8aea8',
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '11px', lg: '12px', xl: '14px' },
                  color: '#6b7470',
                }}
              >
                {renewalDate}
              </Typography>
            </Box>

            {/* Account owner row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '10px', lg: '10px', xl: '12px' },
                  fontWeight: 700,
                  color: '#6b7470',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Account Owner
              </Typography>
              <Box
                sx={{
                  width: { xs: 16, lg: 18, xl: 22 },
                  height: { xs: 16, lg: 18, xl: 22 },
                  borderRadius: '50%',
                  backgroundColor: avatarBg,
                  color: avatarColor,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: { xs: '7px', lg: '8px', xl: '9px' },
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {getInitials(ownerName)}
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: { xs: '12px', lg: '13px', xl: '14px' },
                  fontWeight: 600,
                  color: '#08231B',
                }}
              >
                {ownerName}
              </Typography>
              <Typography sx={{ color: '#a8aea8', fontSize: '13px' }}>·</Typography>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: { xs: '11px', lg: '12px', xl: '13px' },
                  color: '#6b7470',
                }}
              >
                {ownerEmail}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
